import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleX } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import "./factorNodeEditMode.scss";
import { useDynamicPricingContext } from "../../DynamicPricingContext";
import { useAutoFocusElement } from "./useAutoFocusElement";
import { difference, intersection, uniq } from "lodash";
import { useTranslation } from "react-i18next";

const FACTOR_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export type FactorDayValue = (typeof FACTOR_DAYS)[number];

export interface IFactorDayValueType {
  valueType: "day";
  days: FactorDayValue[];
}

interface IFactorNodeDayEditModeProps {
  parentId: string;
  id: string;
  defaultDays: FactorDayValue[];
  onConfirm: (value: IFactorDayValueType) => void;
  onCancel: () => void;
}

function isFactorDayValue(value: string): value is FactorDayValue {
  return FACTOR_DAYS.some(day => day === value);
}

export function getFactorDayValues(value: string): FactorDayValue[] {
  if (value == null || value === "") {
    return [];
  }

  const tokens = value.replace(/\s/g, "").split(",");

  return tokens.filter(isFactorDayValue);
}

export function isFactorDaysValid(days: FactorDayValue[]) {
  return days?.length > 0 && difference(days, FACTOR_DAYS).length === 0;
}

export function getFactorDayNumber(day: FactorDayValue) {
  return FACTOR_DAYS.findIndex(factorDay => factorDay === day);
}

export function formatFactorDayShortName(day: FactorDayValue, language: string) {
  const dayNumber = getFactorDayNumber(day);

  if (dayNumber < 0) {
    return "";
  }

  const sundayOffsetDate = new Date(2024, 0, 7 + dayNumber);

  return new Intl.DateTimeFormat(language, { weekday: "short" }).format(sundayOffsetDate);
}

function getFactorDayShortNameInitial(day: FactorDayValue, language: string) {
  const formattedDay = formatFactorDayShortName(day, language);

  if (formattedDay === "") {
    return "";
  }

  return formattedDay.charAt(0).toLocaleUpperCase(language);
}

export default function FactorNodeDayEditMode(props: IFactorNodeDayEditModeProps) {
  const { parentId, id, defaultDays, onConfirm, onCancel } = props;
  const { i18n } = useTranslation();

  const dynamicPricingContext = useDynamicPricingContext();

  const autoFocusElementRef = useAutoFocusElement<HTMLButtonElement>();

  const [days, setDays] = useState<FactorDayValue[]>(() => [...defaultDays]);

  const siblingDays: FactorDayValue[] = useMemo(
    () =>
      uniq(dynamicPricingContext
        .getFactorSiblings(parentId, id)
        .reduce<FactorDayValue[]>((days, sibling) => {
          if (sibling.data.valueType === "day") {
            days.push(...sibling.data.days);
          }

          return days;
        }, [])),
    [parentId, id],
  );

  const autoFocusElementIndex = useMemo(() => {
    if (days?.length > 0) {
      return FACTOR_DAYS.findIndex(factorDay => days.includes(factorDay));
    } else {
      return FACTOR_DAYS.findIndex(factorDay => !siblingDays.includes(factorDay));
    }
  }, []);

  const confirmDisabled = !isFactorDaysValid(days) || intersection(siblingDays, days).length > 0;

  function toggleDay(factorDay: FactorDayValue) {
    let updatedDays = [...days];

    if (updatedDays.includes(factorDay)) {
      updatedDays = updatedDays.filter(day => day !== factorDay);
    } else {
      updatedDays.push(factorDay);
      updatedDays.sort((prev: FactorDayValue, next: FactorDayValue) => getFactorDayNumber(prev) - getFactorDayNumber(next));
    }

    setDays(updatedDays);
  }

  return (
    <div className="factor-node-edit-mode">
      <div className="factor-node-edit-mode-day-selector">
        {FACTOR_DAYS.map((factorDay, index) => (
          <button
            ref={index === autoFocusElementIndex ? autoFocusElementRef : null}
            key={factorDay}
            type="button"
            className={classNames("factor-node-edit-mode-day", {
              "factor-node-edit-mode-day-selected": days.includes(factorDay),
            })}
            disabled={siblingDays.includes(factorDay)}
            onClick={() => toggleDay(factorDay)}
          >
            {getFactorDayShortNameInitial(factorDay, i18n.language)}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="factor-node-edit-mode-confirm"
        disabled={confirmDisabled}
        onClick={() =>
          onConfirm({
            valueType: "day",
            days,
          })
        }
      >
        <FontAwesomeIcon icon={faCircleCheck} />
      </button>
      <button type="button" className="factor-node-edit-mode-cancel" onClick={onCancel}>
        <FontAwesomeIcon icon={faCircleX} />
      </button>
    </div>
  );
}
